import { FC, useEffect, useState } from 'react'
import { PageTitle } from '../../../../_metronic/layout/core'
import { Button } from '../../../components/Button/Button'
import Swal from 'sweetalert2'
import { GET, POST, PUT } from '../../../services/api'
import { Loading } from '../../../components/Loading/Loading'
import { useNavigate, useParams } from 'react-router-dom'
import { i18n } from '../../../translate/i18n'
import { BreadCrumbs } from '../../../components/BreadCrumbs/breadCrumbrs'
import {
  RoomStatus,
  RoomType,
  getStatusLabel,
  getTypeLabel
} from './types'
import { Input } from '../../../components/Input/Input'
import uuid from 'react-uuid'
import { HospitalityTabs } from '../../../components/HospitalityTabs'

const EditHotelRoomContent: FC = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [loading, setLoading] = useState(false)
  const [originalStatus, setOriginalStatus] = useState<RoomStatus | null>(null)
  const [activeTab, setActiveTab] = useState('basic')
  const [formData, setFormData] = useState({
    roomNumber: '',
    status: RoomStatus.AVAILABLE,
    capacity: '',
    price: '',
    image: '',
    type: RoomType.SINGLE,
  })

  const [image, setImage] = useState<File | string | null>(null)
  const [firstStateImage, setFirstStateImage] = useState<File | string | null>(null)

  const handleInputChange = (field: string, value: string | RoomStatus | RoomType) => {
    setFormData({
      ...formData,
      [field]: value,
    })
  }

  const validateForm = () => {
    if (!formData.roomNumber.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Número do quarto é obrigatório!',
      })
      return false
    }

    if (formData.roomNumber.length > 20) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Número do quarto deve ter no máximo 20 caracteres!',
      })
      return false
    }

    if (!formData.capacity || parseInt(formData.capacity) < 1) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Capacidade é obrigatória e deve ser maior que zero!',
      })
      return false
    }

    if (!formData.price || parseFloat(formData.price) < 0 || parseFloat(formData.price) === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Preço é obrigatório e deve ser maior ou igual a zero!',
      })
      return false
    }

    if (!formData.type) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Tipo do quarto é obrigatório!',
      })
      return false
    }

    if (!formData.status) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Status do quarto é obrigatório!',
      })
      return false
    }

    return true
  }

  const getHotelRoom = async () => {
    try {
      setLoading(true)
      const response = await GET(`/hotel-room/${id}`)

      if (response.success) {
        const room = response.dados[0]
        const roomStatus = room.status || RoomStatus.AVAILABLE
        setOriginalStatus(roomStatus)
        setFormData({
          roomNumber: room.roomNumber || '',
          status: roomStatus,
          image: room.image || '',
          capacity: room.capacity ? room.capacity.toString() : '',
          price: room.price ? room.price.toString() : '',
          type: room.type || RoomType.SINGLE,
        })

        if (room.image) {
          setImage(room.image)
          setFirstStateImage(room.image)
        }
      } else {
        throw new Error(response.message || 'Erro ao carregar quarto')
      }

      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: e.message || 'Erro ao carregar os dados do quarto!',
      }).then(() => {
        navigate('/hospitality/hotel-room')
      })
      console.log(e)
    }
  }

  const validateStatusChange = async (): Promise<boolean> => {
    // Se o status não mudou, não precisa validar
    if (originalStatus === formData.status) {
      return true
    }

    // Se o status original era OCCUPIED e está sendo alterado
    if ((originalStatus === RoomStatus.OCCUPIED || originalStatus === RoomStatus.RESERVED) &&
      (formData.status !== RoomStatus.OCCUPIED && formData.status !== RoomStatus.RESERVED)) {
      const result = await Swal.fire({
        icon: 'warning',
        title: 'Atenção - Quarto Ocupado',
        html: `
          <p><strong>O quarto está atualmente OCUPADO.</strong></p>
          <p>Alterar o status pode causar <strong>inconsistências no sistema</strong>, uma vez que o quarto já está ocupado por um hóspede.</p>
          <p>Tem certeza que deseja continuar com esta alteração?</p>
        `,
        showCancelButton: true,
        confirmButtonText: 'Sim, alterar mesmo assim',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
      })
      return result.isConfirmed
    }

    // Para outros status sendo alterados para um status que não seja AVAILABLE
    if (formData.status !== RoomStatus.AVAILABLE) {
      const statusLabels = {
        [RoomStatus.RESERVED]: 'Reservado',
        [RoomStatus.MAINTENANCE]: 'Manutenção',
        [RoomStatus.OCCUPIED]: 'Ocupado'
      }

      const result = await Swal.fire({
        icon: 'info',
        title: 'Alteração de Status',
        html: `
          <p>O quarto será alterado para <strong>${statusLabels[formData.status as keyof typeof statusLabels] || formData.status}</strong>.</p>
          <p><strong>Importante:</strong> Este quarto não será listado para novos check-ins enquanto estiver com este status.</p>
          <p>Deseja continuar?</p>
        `,
        showCancelButton: true,
        confirmButtonText: 'Sim, alterar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#6c757d',
      })
      return result.isConfirmed
    }

    return true
  }

  const updateHotelRoom = async () => {
    if (!validateForm()) return

    // Validar mudança de status antes de prosseguir
    const statusChangeApproved = await validateStatusChange()
    if (!statusChangeApproved) return

    try {
      setLoading(true)

      let imageRes = {
        success: false,
        image: firstStateImage,
      }
      const payload = {
        roomNumber: formData.roomNumber.trim(),
        status: formData.status,
        capacity: formData.capacity ? parseInt(formData.capacity) : undefined,
        price: formData.price ? parseFloat(formData.price) : undefined,
        type: formData.type,
        image: imageRes.success ? imageRes.image : typeof image === 'string' ? image : undefined,
      }

      // Upload da imagem se foi alterada
      if (image !== firstStateImage && image instanceof File) {
        if (firstStateImage) {
          await POST('/upload-images/delete-one', {
            url: firstStateImage,
          })
        }

        const formData = new FormData()
        formData.append('file', image)
        formData.append('nameImage', `room-${payload.roomNumber}-${uuid()}.jpg`)
        formData.append('originalSize', 'true')
        imageRes = await POST('/upload-images/upload-one', formData)
      }

      payload.image = imageRes.success ? imageRes.image : typeof image === 'string' ? image : undefined



      const response = await PUT(`/hotel-room/${id}`, payload)

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: 'Quarto atualizado',
          text: 'Quarto atualizado com sucesso!',
        }).then(() => {
          navigate('/hospitality/hotel-room')
        })
      } else {
        throw new Error(response.message || 'Erro ao atualizar quarto')
      }

      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: e.message || 'Erro ao atualizar o quarto!',
      })
      console.log(e)
    }
  }

   const resizeImage = (file: File, targetWidth: number, targetHeight: number): Promise<File> => {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')!
        const img = new Image()
  
        img.onload = () => {
          const { naturalWidth: width, naturalHeight: height } = img
  
          // Calcular o crop centralizado
          const sourceRatio = width / height
          const targetRatio = targetWidth / targetHeight
  
          let sourceWidth, sourceHeight, sourceX, sourceY
  
          if (sourceRatio > targetRatio) {
            // Imagem mais larga - cortar nas laterais
            sourceHeight = height
            sourceWidth = height * targetRatio
            sourceX = (width - sourceWidth) / 2
            sourceY = 0
          } else {
            // Imagem mais alta - cortar em cima e embaixo
            sourceWidth = width
            sourceHeight = width / targetRatio
            sourceX = 0
            sourceY = (height - sourceHeight) / 2
          }
  
          // Configurar canvas
          canvas.width = targetWidth
          canvas.height = targetHeight
  
          // Desenhar imagem redimensionada e cortada no centro
          ctx.drawImage(
            img,
            sourceX, sourceY, sourceWidth, sourceHeight,
            0, 0, targetWidth, targetHeight
          )
  
          // Converter canvas para blob e depois para File
          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              })
              resolve(resizedFile)
            }
          }, file.type, 0.9)
        }
  
        img.src = URL.createObjectURL(file)
      })
    }
  
    const validateImage = (file: File): Promise<File | null> => {
      return new Promise((resolve) => {
        // Verificar tamanho do arquivo (máximo 2MB)
        const maxSizeInBytes = 2 * 1024 * 1024 // 2MB
        if (file.size > maxSizeInBytes) {
          Swal.fire({
            icon: 'warning',
            title: String(i18n.t('hospitality_publicity_create.validation.title')),
            text: String(i18n.t('hospitality_publicity_create.validation.image_size_error')),
          })
          resolve(null)
          return
        }
  
        // Verificar dimensões da imagem
        const img = new Image()
        img.onload = async () => {
          const width = img.naturalWidth
          const height = img.naturalHeight
  
          // Dimensões esperadas: aproximadamente 720x600 (tolerância de ±10%)
          const expectedWidth = 1032
          const expectedHeight = 336
          const tolerance = 0.1 // 10%
  
          const minWidth = expectedWidth * (1 - tolerance)
          const maxWidth = expectedWidth * (1 + tolerance)
          const minHeight = expectedHeight * (1 - tolerance)
          const maxHeight = expectedHeight * (1 + tolerance)
  
          if (width < minWidth || width > maxWidth || height < minHeight || height > maxHeight) {
            // Perguntar se o usuário quer redimensionar
            const result = await Swal.fire({
              icon: 'question',
              title: String(i18n.t('hospitality_publicity_create.validation.title')),
              html: `
                  <p>${String(i18n.t('hospitality_publicity_create.validation.image_dimensions_error', {
                current: `${width}x${height}`,
                expected: `${expectedWidth}x${expectedHeight}`
              }).replace('200x112', `${expectedWidth}x${expectedHeight}`))}</p>
                  <p><strong>Deseja redimensionar a imagem automaticamente?</strong></p>
                  <p><small>A imagem será cortada no centro para se ajustar às dimensões necessárias.</small></p>
                `,
              showCancelButton: true,
              confirmButtonText: 'Sim, redimensionar',
              cancelButtonText: 'Não, escolher outra imagem',
              confirmButtonColor: '#3085d6',
              cancelButtonColor: '#d33'
            })
  
            if (result.isConfirmed) {
              try {
                const resizedFile = await resizeImage(file, expectedWidth, expectedHeight)
                resolve(resizedFile)
              } catch (error) {
                Swal.fire({
                  icon: 'error',
                  title: 'Erro',
                  text: 'Ocorreu um erro ao redimensionar a imagem. Tente novamente.',
                })
                resolve(null)
              }
            } else {
              resolve(null)
            }
            return
          }
  
          resolve(file)
        }
  
        img.onerror = () => {
          Swal.fire({
            icon: 'warning',
            title: String(i18n.t('hospitality_publicity_create.validation.title')),
            text: String(i18n.t('hospitality_publicity_create.validation.image_invalid')),
          })
          resolve(null)
        }
  
        img.src = URL.createObjectURL(file)
      })
    }

  useEffect(() => {
    if (id) {
      getHotelRoom()
    }
  }, [id])

  const renderImageTab = () => (
    <div className='row'>
      <div className='col-md-6'>
        <Input
          inputAttr={{
            type: 'image',
          }}
          label={i18n.t('hospitality_hotel_room.image')}
          value={image}
          change={async (value: React.BaseSyntheticEvent) => {
            const file = value.target.files[0]
            if (file) {
              const result = await validateImage(file)
              if (result) {
                // Se result é um File (imagem redimensionada ou original válida)
                setImage(result)
              } else {
                // Limpar o input se a imagem não for válida ou usuário cancelou
                value.target.value = ''
              }
            }
          }}
        />
      </div>
      {image && typeof image !== 'string' && (
        <div className='col-md-6'>
          <div className='border rounded p-3'>
            <img
              src={URL.createObjectURL(image)}
              alt='Preview'
              style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'cover' }}
            />
          </div>
        </div>
      )}
      {image && typeof image === 'string' && (
        <div className='col-md-6'>
          <div className='border rounded p-3'>
            <img
              src={image}
              alt='Imagem atual'
              style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'cover' }}
            />
          </div>
        </div>
      )}
    </div>
  )

  const renderBasicTab = () => (
    <form>
      <div className='row'>
        <div className='col-md-6'>
          <div className='mb-5'>
            <label className='form-label'>{i18n.t('hospitality_hotel_room.label_room_number')}</label>
            <input
              className='form-control'
              name='roomNumber'
              type='text'
              placeholder={i18n.t('hospitality_hotel_room.placeholder_room_number')}
              maxLength={20}
              value={formData.roomNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('roomNumber', e.target.value)}
              required
            />
            <small className='text-muted'>{i18n.t('hospitality_hotel_room.max_20_chars')}</small>
          </div>
        </div>
        <div className='col-md-6'>
          <div className='mb-5'>
            <label className='form-label'>{i18n.t('hospitality_hotel_room.label_status')}</label>
            <select
              className='form-select'
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value as RoomStatus)}
            >
              {Object.values(RoomStatus).map((status) => (
                <option key={status} value={status}>
                  {getStatusLabel(status)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className='row'>
        <div className='col-md-4'>
          <div className='mb-5'>
            <label className='form-label'>{i18n.t('hospitality_hotel_room.label_capacity')}</label>
            <input
              className='form-control'
              name='capacity'
              type='number'
              placeholder={i18n.t('hospitality_hotel_room.placeholder_capacity')}
              min={1}
              value={formData.capacity}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('capacity', e.target.value)}
            />
          </div>
        </div>
        <div className='col-md-4'>
          <div className='mb-5'>
            <label className='form-label'>{i18n.t('hospitality_hotel_room.label_price')}</label>
            <input
              className='form-control'
              name='price'
              type='number'
              step='0.01'
              placeholder={i18n.t('hospitality_hotel_room.placeholder_price')}
              min={0}
              value={formData.price}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('price', e.target.value)}
            />
          </div>
        </div>
        <div className='col-md-4'>
          <div className='mb-5'>
            <label className='form-label'>{i18n.t('hospitality_hotel_room.label_type')}</label>
            <select
              className='form-select'
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value as RoomType)}
            >
              {Object.values(RoomType).map((type) => (
                <option key={type} value={type}>
                  {getTypeLabel(type)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </form>
  )

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('hospitality_hotel_room.edit_title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('hospitality_hotel_room.edit_subtitle').replace('{room}', id || '')}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={i18n.t(`buttons.back`)}
                  color='info'
                  iconColor='#FFF'
                  icon='fas fa-arrow-left'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('/hospitality/hotel-room')}
                  btnClass='mb-5 fw-bolder me-3'
                />
              </div>
            </div>
            <div className='card-body'>
              <HospitalityTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                tabs={[
                  {
                    id: 'basic',
                    label: i18n.t('hotel_tour.tab_basic'),
                    icon: 'fas fa-info-circle',
                    content: renderBasicTab(),
                    hasError: false,
                  },
                  {
                    id: 'image',
                    label: i18n.t('hotel_tour.tab_image'),
                    icon: 'fas fa-image',
                    content: renderImageTab(),
                    hasError: false,
                  },
                ]}
              />

              <div className='d-flex justify-content-end'>
                <Button
                  text={i18n.t(`buttons.cancel`)}
                  color='danger'
                  size='small'
                  horizontalPadding={8}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('/hospitality/hotel-room')}
                  btnClass='me-3'
                />
                <Button
                  text={i18n.t(`buttons.create`)}
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-save'
                  size='small'
                  horizontalPadding={8}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={updateHotelRoom}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const EditHotelRoom: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          { title: i18n.t('hospitality_hotel_room.breadcrumb_dashboard'), path: 'dashboard', isActive: false },
          { title: i18n.t('hospitality_hotel_room.breadcrumb_hotel_room'), path: 'hotel-room', isActive: false },
          { title: i18n.t('hospitality_hotel_room.breadcrumb_edit'), path: 'hotel-room/editar', isActive: true },
        ]}
      >
        {i18n.t('hospitality_hotel_room.edit_title')}
      </PageTitle>
      <EditHotelRoomContent />
    </>
  )
}

export { EditHotelRoom }